import React from "react";
import { useTheme } from "../../hooks/ThemeContext";
import TimeService from "../../service/TimeService";
import ToastService from "../../service/ToastService";
import { Utensils, ChevronDown, ChevronUp } from "lucide-react";
import { bestBlackOrWhite } from "../../utils/contrastColor";

type Props = {
    productData: any,
    productId: string,
    productName: string,
    productDescription?: string,        
    productThumbnailUrl?: string,       
    productPrice: number,
    currencySymbol: string,
    menus: string[],
    activeMenu: string,
    additionalOptions?: any[],
    userChoices?: any[],
    addToCart: any,
    setModalOpen: any,
    menuSettings: any[],
    openingHours: any,
    menuOnly?: boolean,
    onClose: () => void
}

const ProductCardAdditionalOptions = (props: Props) => {

    const getCategoryName = (categoryId: string) => {
        // `|| []` + `y?`: entries without categories (e.g. config sentinels)
        // must never crash the whole modal.
        let categoryObj = props.menuSettings.map((x) => x.categories || []).flat().filter((y) => y?.id === categoryId)[0]
        if (categoryObj) {
            return categoryObj.name;
        }
        
        return categoryId;
    }

    const getRandomPastelColor = (seed: string) => {
        // Generate a deterministic hash from the string
        let hash = 0;
        for (let i = 0; i < seed.length; i++) {
            hash = ((hash << 5) - hash) + seed.charCodeAt(i);
            hash = hash & hash;
        }

        // Use the hash to generate HSL values
        const hue = Math.abs(hash % 360);
        // Keep saturation and lightness in pastel range
        const saturation = 65 + (Math.abs((hash >> 8) % 15)); // 65-80%
        const lightness = 80 + (Math.abs((hash >> 16) % 10)); // 80-90%

        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };
    
    const [totalPrice, setTotalPrice] = React.useState(props.productPrice);
    const [selectedAddons, setSelectedAddons] = React.useState<any[]>([]);
    const [quantity, setQuantity] = React.useState(1);

    // Item count tracking: the quantity stepper can't go past what's left
    // of a tracked product (untracked keeps the usual 1-10). The add funnel
    // still guards the cross-cart aggregate.
    const maxQuantity = React.useMemo(() => {
        const p = props.productData?.find((x: any) => x.productId === props.productId);
        return typeof p?.stockCount === "number" ? Math.max(1, Math.min(10, p.stockCount)) : 10;
    }, [props.productData, props.productId]);
    const [additionalRequests, setAdditionalRequests] = React.useState("");
    const [categories, setCategories] = React.useState<string[]>([]);
    const [selectedChoices, setSelectedChoices] = React.useState<{name: string, value: string, parent?: string, taxExemptOnTakeout?: boolean}[]>([]);
    
    // State for tracking which addons have their choices expanded
    const [expandedAddons, setExpandedAddons] = React.useState<{[addonId: string]: boolean}>({});

    // Helper function to check if a choice is selected
    const isChoiceSelected = (optionName: string, choiceValue: string) => {
        return selectedChoices.some(choice => choice.name === optionName && choice.value === choiceValue);
    };

    // Helper function to check if an add-on choice is selected
    const isAddonChoiceSelected = (addonId: string, optionName: string, choiceValue: string) => {
        return selectedChoices.some(choice => 
            choice.parent === addonId && choice.name === optionName && choice.value === choiceValue
        );
    };

    // ── Auto-advance ────────────────────────────────────────────────────
    // Long option lists hide what's below the fold — customers didn't know
    // to scroll. Completing a section (a SELECT_ONE pick, or a SELECT_LIMIT
    // group reaching its limit) scrolls the NEXT section's heading to the
    // top of the modal: next option group → add-ons → additional requests.
    // Multi-selects (SELECT_ANY, add-ons) never auto-advance mid-picking.
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const choiceSectionRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const addonsSectionRef = React.useRef<HTMLDivElement | null>(null);
    const requestsSectionRef = React.useRef<HTMLDivElement | null>(null);

    const scrollSectionToTop = (el: HTMLElement | null) => {
        const container = scrollRef.current;
        if (!container || !el) return;
        const top = container.scrollTop + el.getBoundingClientRect().top - container.getBoundingClientRect().top - 10;
        container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const advanceFrom = (optionName: string) => {
        const idx = (props.userChoices || []).findIndex((o: any) => o.name === optionName);
        window.requestAnimationFrame(() => {
            if (idx >= 0 && idx + 1 < (props.userChoices || []).length) {
                scrollSectionToTop(choiceSectionRefs.current[idx + 1]);
            } else if (props.additionalOptions && props.additionalOptions.length > 0) {
                scrollSectionToTop(addonsSectionRef.current);
            } else {
                scrollSectionToTop(requestsSectionRef.current);
            }
        });
    };

    // Helper function to handle choice selection
    const handleChoiceToggle = (optionName: string, choiceValue: string, optionType: string, limit?: number, taxExemptOnTakeout?: boolean) => {
        // Advance decisions need the PRE-update state (setState runs async).
        const wasSelected = isChoiceSelected(optionName, choiceValue);
        const currentCount = selectedChoices.filter(c => c.name === optionName && !c.parent).length;
        if (!wasSelected) {
            if (optionType === 'SELECT_ONE') advanceFrom(optionName);
            else if (optionType === 'SELECT_LIMIT' && currentCount + 1 >= (limit || 1) && currentCount < (limit || 1)) advanceFrom(optionName);
        }
        setSelectedChoices(prevChoices => {
            const existingChoices = prevChoices.filter(choice => choice.name === optionName);
            const otherChoices = prevChoices.filter(choice => choice.name !== optionName);
            
            if (optionType === 'SELECT_ONE') {
                // For SELECT_ONE, replace any existing choice for this option
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Deselect if already selected
                    return otherChoices;
                } else {
                    // Select this choice (replacing any previous choice for this option)
                    return [...otherChoices, { name: optionName, value: choiceValue, taxExemptOnTakeout: taxExemptOnTakeout }];
                }
            } else if (optionType === 'SELECT_ANY') {
                // For SELECT_ANY, toggle the specific choice
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Remove this choice
                    return [...otherChoices, ...existingChoices.filter(choice => choice.value !== choiceValue)];
                } else {
                    // Add this choice
                    return [...prevChoices, { name: optionName, value: choiceValue, taxExemptOnTakeout: taxExemptOnTakeout }];
                }
            } else if (optionType === 'SELECT_LIMIT') {
                // For SELECT_LIMIT, check limit before adding
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Remove this choice
                    return [...otherChoices, ...existingChoices.filter(choice => choice.value !== choiceValue)];
                } else {
                    // Check if we can add more choices
                    if (existingChoices.length < (limit || 1)) {
                        return [...prevChoices, { name: optionName, value: choiceValue, taxExemptOnTakeout: taxExemptOnTakeout }];
                    } else {
                        // If at limit, show a toast message and don't add
                        ToastService.showToast(`You can only select up to ${limit} options for ${optionName}`, {
                            style: {
                                backgroundColor: "#ff9800",
                                color: "white",
                                minHeight: "60px",
                                borderRadius: "15px",
                            },
                        });
                        return prevChoices;
                    }
                }
            }
            return prevChoices;
        });
    };

    // Handler for add-on choice selection
    const handleAddonChoiceToggle = (addonId: string, optionName: string, choiceValue: string, optionType: string, limit?: number, taxExemptOnTakeout?: boolean) => {
        setSelectedChoices(prevChoices => {
            // Get choices for this specific addon option
            const existingChoices = prevChoices.filter(choice => 
                choice.parent === addonId && choice.name === optionName
            );
            // Get all other choices (including main product choices and other addon choices)
            const otherChoices = prevChoices.filter(choice => 
                !(choice.parent === addonId && choice.name === optionName)
            );
            
            let newChoices = prevChoices;
            
            if (optionType === 'SELECT_ONE') {
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Deselect
                    newChoices = otherChoices;
                } else {
                    // Select this choice (replacing any previous choice for this option)
                    newChoices = [...otherChoices, { name: optionName, value: choiceValue, parent: addonId, taxExemptOnTakeout: taxExemptOnTakeout }];
                }
            } else if (optionType === 'SELECT_ANY') {
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Remove this choice
                    newChoices = [...otherChoices, ...existingChoices.filter(choice => choice.value !== choiceValue)];
                } else {
                    // Add this choice
                    newChoices = [...prevChoices, { name: optionName, value: choiceValue, parent: addonId, taxExemptOnTakeout: taxExemptOnTakeout }];
                }
            } else if (optionType === 'SELECT_LIMIT') {
                const isCurrentlySelected = existingChoices.some(choice => choice.value === choiceValue);
                if (isCurrentlySelected) {
                    // Remove this choice
                    newChoices = [...otherChoices, ...existingChoices.filter(choice => choice.value !== choiceValue)];
                } else {
                    // Check if we can add more choices
                    if (existingChoices.length < (limit || 1)) {
                        newChoices = [...prevChoices, { name: optionName, value: choiceValue, parent: addonId, taxExemptOnTakeout: taxExemptOnTakeout }];
                    } else {
                        ToastService.showToast(`You can only select up to ${limit} options for ${optionName}`, {
                            style: {
                                backgroundColor: "#ff9800",
                                color: "white",
                                minHeight: "60px",
                                borderRadius: "15px",
                            },
                        });
                        return prevChoices;
                    }
                }
            }
            
            return newChoices;
        });
    };

    const { theme } = useTheme();

    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            backgroundColor: theme.backgroundColor,
            border: "1px solid " + theme.borderColor,
            borderRadius: 8,
            maxWidth: 600,
            height: '100%',
            boxSizing: 'border-box',
            margin: "0 auto",
            position: "relative",
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        },
        scrollContent: {
            padding: 0,
            flex: 1,
            overflowY: 'auto',
            position: 'relative'
        },
        heroContainer: {
            width: '100%',
            padding: '32px 24px 8px',
            display: 'flex',
            justifyContent: 'center',
            boxSizing: 'border-box'
        },
        heroThumbnail: {
            width: 200,
            height: 200,
            maxWidth: '60%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            borderRadius: 16,
            display: 'block'
        },
        contentPadding: {
            padding: 24,
            paddingTop: 20
        },
        description: {
            fontSize: 14,
            lineHeight: 1.55,
            color: theme.textColor,
            opacity: 0.75,
            margin: '0 0 24px 0'
        },
        stickyFooter: {
            position: 'sticky',
            bottom: 0,
            backgroundColor: theme.backgroundColor,
            borderTop: '1px solid ' + theme.borderColor,
            padding: '16px 24px',
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
        },
        closeButton: {
            position: "absolute",
            right: 16,
            top: 16,
            width: 36,
            height: 36,
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            color: "#666",
            borderRadius: "50%",
            transition: "all 0.2s ease",
            zIndex: 10
        },
        title: {
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 24,
            color: theme.textColor
        },
        categoryTitle: {
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 12,
            color: theme.cartTextColor
        },
        optionContainer: {
            marginBottom: 24
        },
        optionItem: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 14,
            marginBottom: 10,
            borderRadius: 12,
            transition: "background-color 0.2s",
            cursor: "pointer",
            backgroundColor: theme.searchBarColor || "#f8f8f8",
            userSelect: "none"
        },
        optionLeft: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            flex: 1
        },
        optionImage: {
            width: 48,
            height: 48,
            borderRadius: 4,
            objectFit: "cover"
        },
        placeholderImage: {
            width: 48,
            height: 48,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666666"
        },
        checkbox: {
            width: 18,
            height: 18,
            cursor: "pointer",
            pointerEvents: "none"
        },
        price: {
            fontWeight: 600,
            color: theme.cartTextColor,
            marginLeft: 12
        },
        textareaContainer: {
            marginBottom: 24
        },
        label: {
            display: "block",
            marginBottom: 8,
            fontWeight: 500,
            color: theme.textColor
        },
        textarea: {
            backgroundColor: theme.searchBarColor || "#f8f8f8",
            // No color = browser-default black, unreadable on dark themes —
            // derive it from the field's own background.
            color: bestBlackOrWhite(theme.searchBarColor || "#f8f8f8"),
            caretColor: bestBlackOrWhite(theme.searchBarColor || "#f8f8f8"),
            border: "1px solid " + theme.borderColor,
            width: "100%",
            padding: 12,
            borderRadius: 6,
            height: 100,
            resize: "none",
            fontSize: 14,
            marginBottom: 8,
            boxSizing: "border-box"
        },
        warning: {
            color: "#dc3545",
            fontSize: 12
        },
        quantityContainer: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 24
        },
        select: {
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff",
            cursor: "pointer"
        },
        stepper: {
            display: "flex",
            alignItems: "center",
            gap: 4,
            border: "1px solid " + theme.borderColor,
            borderRadius: 999,
            padding: 4,
            backgroundColor: theme.cartCardColor
        },
        stepBtn: {
            width: 34,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: "50%",
            background: "transparent",
            color: theme.cartTextColor,
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            userSelect: "none"
        },
        stepValue: {
            minWidth: 28,
            textAlign: "center",
            fontWeight: 600,
            fontSize: 16,
            color: theme.cartTextColor,
            fontVariantNumeric: "tabular-nums"
        },
        button: {
            width: "100%",
            padding: 16,
            border: "none",
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: "0.01em",
            cursor: "pointer"
        },
        addonChoicesContainer: {
            marginLeft: 46,
            marginTop: 8,
            marginBottom: 8,
            padding: 12,
            backgroundColor: theme.cartCardColor || theme.backgroundColor,
            borderRadius: 6,
            border: "1px solid " + theme.borderColor
        },
        addonChoiceItem: {
            display: "flex",
            alignItems: "center",
            padding: 8,
            marginBottom: 4,
            borderRadius: 4,
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: theme.cartCardColor
        },
        addonChoiceItemSelected: {
            display: "flex",
            alignItems: "center",
            padding: 8,
            marginBottom: 4,
            borderRadius: 4,
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: theme.cartCardColor,
            border: "2px solid " + theme.cartButtonColor
        },
        expandButton: {
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: theme.cartTextColor || "#666",
            marginLeft: 8
        }
    };

    const handleOptionToggle = (option: any) => {
        const isSelected = selectedAddons.includes(option.productId);
        if (isSelected) {
            setTotalPrice(currentPrice => currentPrice - option.productPrice);
            setSelectedAddons(prevItems => prevItems.filter(item => item !== option.productId));
            // Clear choices for this addon when deselected
            setSelectedChoices(prevChoices => 
                prevChoices.filter(choice => choice.parent !== option.productId)
            );
            setExpandedAddons(prev => {
                const newExpanded = {...prev};
                delete newExpanded[option.productId];
                return newExpanded;
            });
        } else {
            setTotalPrice(currentPrice => currentPrice + option.productPrice);
            setSelectedAddons(prevItems => [...prevItems, option.productId]);
            // Auto-expand if it has choices
            if (option.userChoices && option.userChoices.length > 0) {
                setExpandedAddons(prev => ({...prev, [option.productId]: true}));
            }
        }
    };

    const toggleAddonExpansion = (addonId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedAddons(prev => ({
            ...prev,
            [addonId]: !prev[addonId]
        }));
    };

    const handleSubmission = () => {
        const requiredChoices = props.userChoices?.filter(option => {
            return option.optionRequired === true && option.type !== 'SELECT_LIMIT';
        }) || [];
        
        console.log("Required choices to validate:", requiredChoices);
        console.log("Current selected choices:", selectedChoices);
        
        const hasAllRequiredChoices = requiredChoices.every(requiredOption => {
            const hasSelection = selectedChoices.some(choice => 
                !choice.parent && choice.name === requiredOption.name
            );
            console.log(`Option "${requiredOption.name}" has selection:`, hasSelection);
            return hasSelection;
        });
        
        console.log("Has all required choices:", hasAllRequiredChoices);
        
        if (!hasAllRequiredChoices) {
            const missingOptions = requiredChoices.filter(requiredOption => 
                !selectedChoices.some(choice => !choice.parent && choice.name === requiredOption.name)
            ).map(option => option.name);
            
            ToastService.showToast(`Please select required options: ${missingOptions.join(', ')}`, {
                style: {
                    backgroundColor: "darkred",
                    color: "white",
                    minHeight: "80px",
                    borderRadius: "15px",
                },
            });
            return;
        }

        // Validate required choices for selected add-ons
        for (const addonId of selectedAddons) {
            const addon = props.additionalOptions?.find(opt => opt.productId === addonId);
            if (addon && addon.userChoices) {
                const requiredAddonChoices = addon.userChoices.filter((option: any) => 
                    option.optionRequired === true && option.type !== 'SELECT_LIMIT'
                );
                
                const addonSelectedChoices = selectedChoices.filter(choice => choice.parent === addonId);
                const hasAllRequiredAddonChoices = requiredAddonChoices.every((requiredOption: any) => 
                    addonSelectedChoices.some(choice => choice.name === requiredOption.name)
                );
                
                if (!hasAllRequiredAddonChoices) {
                    const missingOptions = requiredAddonChoices.filter((requiredOption: any) => 
                        !addonSelectedChoices.some(choice => choice.name === requiredOption.name)
                    ).map((option: any) => option.name);
                    
                    ToastService.showToast(`Please select required options for ${addon.productName}: ${missingOptions.join(', ')}`, {
                        style: {
                            backgroundColor: "darkred",
                            color: "white",
                            minHeight: "80px",
                            borderRadius: "15px",
                        },
                    });
                    return;
                }
            }
        }

        // Tax Calculation Logic
        let originalProduct = props.productData.filter((product: any) => product.productId === props.productId)[0];

        let selectedAddonsMetadata: any[] = [];

        for (const addonId of selectedAddons) {
            let originalAddonProduct = props.productData.filter((product: any) => product.productId === addonId)[0];

            let taxExemptOnTakeout = false;
            if (originalAddonProduct.taxExemptOnTakeout) {
                const addonSelectedChoices = selectedChoices.filter(choice => choice.parent === addonId);

                if (addonSelectedChoices.every(choice => choice.taxExemptOnTakeout === true)) {
                    taxExemptOnTakeout = true;
                }
            }

            selectedAddonsMetadata.push({
                id: addonId,
                taxExemptOnTakeout: taxExemptOnTakeout,
                takeoutTaxableAmount: taxExemptOnTakeout ? 0 : originalAddonProduct.productPrice,
                taxableAmount: originalAddonProduct.productPrice
            })
        }
        console.log("123: checking store is open from productCardAdditionalOptions ...")
        // openingHours arrives WITH the store config — before that it's an
        // empty array, which checkStoreIsOpen reads as "closed" (that false
        // "Store is currently closed!" right after page load). No hours
        // loaded/configured = don't enforce; the per-mode gate in addToCart
        // and the server still stand behind this.
        const hoursKnown = Array.isArray(props.openingHours) && props.openingHours.length > 0;
        (hoursKnown ? TimeService.checkStoreIsOpen(props.openingHours) : Promise.resolve(true)).then((response) => {
            if (response !== true) {
                props.setModalOpen(false);
                ToastService.showToast(`Store is currently closed!`, {
                    style: {
                        backgroundColor: "darkred",
                        color: "white",
                        minHeight: "80px",
                        borderRadius: "15px",
                    },
                });
            } else {
                TimeService.checkAnyMenuActive(props.menuSettings, props.menus).then((response) => {
                    if (response == true) {
                        if (props.productId && additionalRequests.length <= 200) {
                            // addToCart can REFUSE (closed fulfilment mode)
                            // and shows its own warning — only announce
                            // success when the item actually landed.
                            const added = props.addToCart({
                                "id": props.productId,
                                "selectedChoices": selectedChoices,
                                "additionalOptionIds": selectedAddons,
                                "additionalOptionsMetadata": selectedAddonsMetadata,
                                "additionalRequests": additionalRequests,
                                taxExemptOnTakeout: originalProduct.taxExemptOnTakeout || false,
                                takeoutTaxableAmount: originalProduct.taxExemptOnTakeout ? 0 : props.productPrice,
                                taxableAmount: props.productPrice,
                                quantity: quantity,
                                menus: props.menus
                            });
                            props.setModalOpen(false);
                            if (added !== false) {
                                ToastService.showToast("Item added to cart!", {
                                    style: {
                                        backgroundColor: "darkgreen",
                                        color: "white",
                                        minHeight: "80px",
                                        borderRadius: "15px",
                                    },
                                });
                            }
                        }
                    } else {
                        props.setModalOpen(false);
                        ToastService.showToast(`Items from ${props.activeMenu} are not available at this time!`, {
                            style: {
                                backgroundColor: "darkred",
                                color: "white",
                                minHeight: "80px",
                                borderRadius: "15px",
                            },
                        });
                    }
                });
            }
        });
    };

    React.useEffect(() => {
        let c: string[] = [];
        if (props.additionalOptions && props.menuSettings) {
            console.log("AO: ", props.additionalOptions)
            props.additionalOptions
                .filter((x) => !(x.isDeleted || x.isDisabled || x.outOfStock))
                .forEach((option) => {
                    const categoryId = option.category;
                    
                    if (categoryId) {
                        // Check if this category is enabled and not deleted
                        const categoryObj = props.menuSettings
                            .map((x) => x.categories)
                            .flat()
                            .find((y) => y.id === categoryId);
                        
                        // Only add category if it exists and is enabled
                        if (categoryObj && categoryObj.enabled !== false && !categoryObj.deleted) {
                            if (!c.includes(categoryId)) {
                                c.push(categoryId);
                            }
                        }
                    } else {
                        // For items without a category
                        if (!c.includes("Extras:")) {
                            c.push("Extras:");
                        }
                    }
                });
            
            setCategories(c);
        }
    }, [props.additionalOptions, props.menuSettings]);

    React.useEffect(() => {
        console.log("User Choices: ", props.userChoices)
    }, [props.userChoices]);

    return (
        <div
            className="pcao-root"
            style={{
                ...styles.container,
                ["--pcao-accent" as any]: theme.cartButtonColor,
                ["--pcao-accent-text" as any]: theme.cartButtonTextColor,
                ["--pcao-border" as any]: theme.borderColor,
                ["--pcao-card" as any]: theme.cartCardColor,
                ["--pcao-text" as any]: theme.cartTextColor,
            }}
        >
            <style>{`
                @keyframes pcaoIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .pcao-root { animation: pcaoIn 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }

                .pcao-option {
                    border: 1px solid transparent;
                    transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1),
                                box-shadow 0.2s ease, border-color 0.2s ease;
                }
                @media (hover: hover) {
                    .pcao-option:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 8px 18px -12px rgba(0, 0, 0, 0.35);
                    }
                }
                .pcao-option:active { transform: scale(0.99); }
                .pcao-option.selected {
                    border-color: var(--pcao-accent);
                    box-shadow: 0 0 0 1px var(--pcao-accent) inset;
                }

                .pcao-choice {
                    transition: background-color 0.15s ease, border-color 0.2s ease, transform 0.12s ease;
                }
                .pcao-choice:active { transform: scale(0.99); }

                .pcao-close { transition: transform 0.2s ease, background 0.2s ease; }
                @media (hover: hover) { .pcao-close:hover { transform: scale(1.1); } }

                .pcao-add-btn {
                    transition: transform 0.15s cubic-bezier(0.22, 1, 0.36, 1),
                                box-shadow 0.2s ease, filter 0.2s ease;
                }
                @media (hover: hover) {
                    .pcao-add-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 12px 24px -12px rgba(0, 0, 0, 0.45);
                        filter: brightness(1.04);
                    }
                }
                .pcao-add-btn:active { transform: translateY(0) scale(0.98); }

                .pcao-step-btn { transition: transform 0.12s ease, background 0.2s ease, opacity 0.2s ease; }
                @media (hover: hover) { .pcao-step-btn:hover:not(:disabled) { background: var(--pcao-border); } }
                .pcao-step-btn:active:not(:disabled) { transform: scale(0.9); }
                .pcao-step-btn:disabled { opacity: 0.35; cursor: not-allowed; }

                @media (prefers-reduced-motion: reduce) {
                    .pcao-root, .pcao-option, .pcao-choice, .pcao-add-btn,
                    .pcao-close, .pcao-step-btn {
                        animation: none !important;
                        transition: none !important;
                    }
                }
            `}</style>
            <div style={styles.scrollContent} ref={scrollRef}>
                {props.productThumbnailUrl && (
                    <div style={styles.heroContainer}>
                        <img
                            src={props.productThumbnailUrl}
                            alt={props.productName}
                            style={styles.heroThumbnail}
                        />
                    </div>
                )}

                <button 
                    onClick={props.onClose}
                    aria-label="Close"
                    className="pcao-close"
                    style={{
                        ...styles.closeButton,
                        background: props.productThumbnailUrl ? "rgba(0, 0, 0, 0.5)" : "none",
                        color: props.productThumbnailUrl ? "#fff" : "#666"
                    }}
                >
                    ×
                </button>

                <div style={styles.contentPadding}>
                    <h2 style={styles.title}>{props.productName}</h2>

                    {props.productDescription && (
                        <p style={styles.description}>{props.productDescription}</p>
                    )}

                {props.userChoices && props.userChoices.length > 0 && (
                    <div style={styles.optionContainer}>
                        {props.userChoices.map((userOption, optionIndex) => (
                            <div key={optionIndex} style={{marginBottom: 20}} ref={(el) => { choiceSectionRefs.current[optionIndex] = el; }}>
                                <p style={styles.categoryTitle}>
                                    {userOption.name}
                                    {userOption.optionRequired && userOption.type !== 'SELECT_LIMIT' && (
                                        <span style={{color: '#dc3545'}}> *</span>
                                    )}
                                    {userOption.type === 'SELECT_LIMIT' && (
                                        <span style={{fontSize: 12, color: '#666', marginLeft: 8}}>
                                            (Select up to {userOption.limit})
                                        </span>
                                    )}
                                </p>
                                
                                {userOption.choices && userOption.choices.map((choice: string, choiceIndex: number) => {
                                    const isSelected = isChoiceSelected(userOption.name, choice);
                                    const inputType = userOption.type === 'SELECT_ONE' ? 'radio' : 'checkbox';

                                    let taxExemptOnTakeout = false;
                                    if (userOption.choicesMetadata) {
                                        taxExemptOnTakeout = userOption.choicesMetadata[choiceIndex].taxExempt
                                    }
                                    
                                    return (
                                        <div 
                                            key={choiceIndex} 
                                            className={`pcao-option ${isSelected ? "selected" : ""}`}
                                            style={{
                                                ...styles.optionItem,
                                                backgroundColor: theme.cartCardColor,
                                                cursor: props.menuOnly ? "default" : "pointer"
                                            }}
                                            onClick={props.menuOnly ? undefined : () => handleChoiceToggle(userOption.name, choice, userOption.type, userOption.limit, taxExemptOnTakeout)}
                                        >
                                            <div style={styles.optionLeft}>
                                                {!props.menuOnly && (
                                                <input
                                                    type={inputType}
                                                    style={styles.checkbox}
                                                    checked={isSelected}
                                                    name={userOption.name}
                                                    readOnly
                                                />
                                                )}
                                                <span>{choice}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

                {props.additionalOptions && props.additionalOptions.length > 0 && (
                    <div style={styles.optionContainer} ref={addonsSectionRef}>
                        {categories.map((category, categoryIdx) => (
                            <div key={categoryIdx} style={{marginBottom: 20}}>
                                <p style={styles.categoryTitle}>{getCategoryName(category)}</p>
                                
                                {props.additionalOptions
                                    ?.filter((o) => o.category === category || (category === "Extras:" && !o.heading))
                                    .map((option, index) => {
                                        const isSelected = selectedAddons.includes(option.productId);
                                        const hasChoices = option.userChoices && option.userChoices.length > 0;
                                        const isExpanded = expandedAddons[option.productId];
                                        
                                        return (
                                            <div key={index}>
                                                <div 
                                                    className={`pcao-option ${isSelected ? "selected" : ""}`}
                                                    style={{
                                                        ...styles.optionItem,
                                                        backgroundColor: isSelected ? theme.cartCardColor : theme.cartCardColor
                                                    }}
                                                    onClick={() => handleOptionToggle(option)}
                                                >
                                                    <div style={styles.optionLeft}>
                                                        {!props.menuOnly && (
                                                        <input
                                                            type="checkbox"
                                                            style={styles.checkbox}
                                                            checked={isSelected}
                                                            readOnly
                                                        />
                                                        )}
                                                        {option.productThumbnailUrl ? (
                                                            <img 
                                                                src={option.productThumbnailUrl} 
                                                                alt={option.productName}
                                                                style={styles.optionImage}
                                                            />
                                                        ) : (
                                                            <div 
                                                                style={{
                                                                    ...styles.placeholderImage,
                                                                    backgroundColor: getRandomPastelColor(option.productId + option.productName)
                                                                }}
                                                            >
                                                                <Utensils size={16} />
                                                            </div>
                                                        )}
                                                        <span>{option.productName}</span>
                                                        {hasChoices && isSelected && (
                                                            <div 
                                                                style={styles.expandButton}
                                                                onClick={(e) => toggleAddonExpansion(option.productId, e)}
                                                            >
                                                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                                <span>{isExpanded ? 'Hide' : 'Show'} options</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span style={styles.price}>
                                                        {props.currencySymbol + (Math.round(option.productPrice * 100) / 100).toFixed(2)}
                                                    </span>
                                                </div>
                                                
                                                {/* Add-on choices section */}
                                                {isSelected && hasChoices && isExpanded && (
                                                    <div style={styles.addonChoicesContainer}>
                                                        {option.userChoices.map((userOption: any, optionIndex: number) => (
                                                            <div key={optionIndex} style={{marginBottom: 12}}>
                                                                <p style={{...styles.categoryTitle, fontSize: 14, marginBottom: 8, color: theme.cartTextColor}}>
                                                                    {userOption.name}
                                                                    {userOption.optionRequired && userOption.type !== 'SELECT_LIMIT' && (
                                                                        <span style={{color: '#dc3545'}}> *</span>
                                                                    )}
                                                                    {userOption.type === 'SELECT_LIMIT' && (
                                                                        <span style={{fontSize: 11, color: theme.cartTextColor || '#666', marginLeft: 8}}>
                                                                            (Select up to {userOption.limit})
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                
                                                                {userOption.choices && userOption.choices.map((choice: string, choiceIndex: number) => {
                                                                    const isChoiceSelected = isAddonChoiceSelected(option.productId, userOption.name, choice);
                                                                    const inputType = userOption.type === 'SELECT_ONE' ? 'radio' : 'checkbox';

                                                                    let taxExemptOnTakeout = false;
                                                                    if (userOption.choicesMetadata) {
                                                                        taxExemptOnTakeout = userOption.choicesMetadata[choiceIndex].taxExempt
                                                                    }
                                                                    
                                                                    return (
                                                                        <div 
                                                                            key={choiceIndex} 
                                                                            className="pcao-choice"
                                                                            style={{
                                                                                ...(isChoiceSelected ? styles.addonChoiceItemSelected : styles.addonChoiceItem),
                                                                                cursor: props.menuOnly ? "default" : "pointer"
                                                                            }}
                                                                            onClick={props.menuOnly ? undefined : () => handleAddonChoiceToggle(
                                                                                option.productId, 
                                                                                userOption.name, 
                                                                                choice, 
                                                                                userOption.type, 
                                                                                userOption.limit,
                                                                                taxExemptOnTakeout
                                                                            )}
                                                                        >
                                                                            {!props.menuOnly && (
                                                                            <input
                                                                                type={inputType}
                                                                                style={{...styles.checkbox, width: 16, height: 16}}
                                                                                checked={isChoiceSelected}
                                                                                name={`${option.productId}-${userOption.name}`}
                                                                                readOnly
                                                                            />
                                                                            )}
                                                                            <span style={{marginLeft: 8, fontSize: 14, color: theme.cartTextColor}}>{choice}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        ))}
                    </div>
                )}

                {!props.menuOnly && (
                <div style={styles.textareaContainer} ref={requestsSectionRef}>
                    <label style={styles.label}>Additional Requests</label>
                    <textarea
                        maxLength={200}
                        style={styles.textarea}
                        onChange={(e: any) => setAdditionalRequests(e.target.value)}
                        placeholder="Enter any special requests here..."
                    />
                    <span style={styles.warning}>Your request will be submitted but is not guaranteed</span>
                </div>
                )}
                </div>
            </div>

            {!props.menuOnly && (
            <div style={styles.stickyFooter}>
                <div style={styles.quantityContainer}>
                    <label style={styles.label}>Quantity</label>
                    <div style={styles.stepper}>
                        <button
                            type="button"
                            className="pcao-step-btn"
                            style={styles.stepBtn}
                            aria-label="Decrease quantity"
                            disabled={quantity <= 1}
                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        >
                            −
                        </button>
                        <span style={styles.stepValue} aria-live="polite">{quantity}</span>
                        <button
                            type="button"
                            className="pcao-step-btn"
                            style={styles.stepBtn}
                            aria-label="Increase quantity"
                            disabled={quantity >= maxQuantity}
                            onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))}
                        >
                            +
                        </button>
                    </div>
                </div>

                <button
                    onClick={handleSubmission}
                    className="pcao-add-btn"
                    style={{
                        ...styles.button,
                        backgroundColor: theme.cartButtonColor,
                        color: theme.cartButtonTextColor,
                        marginBottom: 0
                    }}
                >
                    Add to Cart ({props.currencySymbol + (totalPrice * quantity).toFixed(2)})
                </button>
            </div>
            )}
        </div>
    );
};

export default ProductCardAdditionalOptions;